import type { BookingOptions, BookingInfo, BookingValueOddsTypeLimit } from '@/types/booked-bet';
import { OperationType } from '@/types/booked-bet';
import { WebOddsType, IncDecType } from '@nf/utils-common/constants';

abstract class BookingStrategy {
	protected options: BookingOptions;
	protected info: BookingInfo;
	
	constructor(options: BookingOptions, info: BookingInfo) {
		this.options = options;
		this.info = info;
	}

	abstract generateStepSize(value: number): number;
	abstract getOddsTypeLimit(value: number): BookingValueOddsTypeLimit;
	/**
	 * Calculate the allowed range of values based on DisplayHDP or DisplayOdds
	 */
	abstract calculateDefaultRange(value: number, incDecType: IncDecType): Array<number>;
	abstract calculateBookingOdds(
		odds: number, 
		operationType: OperationType, 
		baseOddsType?: WebOddsType
	): number;

	protected get oddsRangeSize() {
		return 10;
	}

	protected get isMainDecimalOdds() {
		return this.info.isDecimalOdds && !this.info.isFixedDecimal;
	}

	calculateWithStep(odds: number, operationType: OperationType) {
		const step = this.generateStepSize(odds);
		const value = operationType === OperationType.Plus 
			? odds + step 
			: odds - step;

		// If it's 0, just return it. It will be filtered out later
		if (value === 0) {
			return 0;
		}

		return value;
	}

	protected generateIncrementedValuesForDecimal(start: number, count: number): Array<number> {
		let currentValue = start;
		const result = [];
		const { max } = this.getOddsTypeLimit(currentValue);
		const hasMax = max !== undefined;
	
		for (let i = 0; i < count; i++) {
			currentValue = this.calculateBookingOdds(currentValue, OperationType.Plus, WebOddsType.MY);
			
			if (currentValue === 0 || (hasMax && currentValue > max)) {
				break;
			}

			result.push(currentValue);

			if (hasMax && currentValue === max) break;
		}
	
		return result;
	};

	protected generateDecrementedValuesForDecimal(start: number, count: number): Array<number> {
		let currentValue = start;
		const result = [];
		const { min } = this.getOddsTypeLimit(currentValue);

		for (let i = 0; i < count; i++) {
			currentValue = this.calculateBookingOdds(currentValue, OperationType.Minus, WebOddsType.MY);
			
			if (min !== undefined && currentValue < min) {
				break;
			}

			result.push(currentValue);
		}

		result.sort((a, b) => a - b);
		return result;
	}
}

export default BookingStrategy;